import React, { useEffect } from 'react';
import { Link, EmptyContent, ErrorHandler } from '@common/components';
import { NetworkStatus, usePaginationHelper, useQuery } from '@common/hooks';
import { StaffingRequestTemplateList } from 'works/legacy/components/staffing-request-template-list';
import {
  PAGINATED_STAFFING_REQUEST_TEMPLATES_QUERY,
  PaginatedStaffingRequestTemplatesResponse,
} from 'works/legacy/graphql/staffing-request-template';
import { buildDialogUrl } from '@common/services/dialogs-service';

export const StaffingTemplatesTable = () => {
  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
    hasAnyFilter,
  } = usePaginationHelper({
    localStorageFilterKey: 'StaffingTemplatesTable',
    defaultPaginationData: {
      defaultPageSize: 10,
      limit: 10,
      offset: 0,
      orderDirection: 'ASC',
      orderField: 'NAME',
    },
    columnsDictionary: {
      name: 'NAME',
      organizationUnit: 'ORGANIZATION_UNIT',
      organizationBusiness: 'LOCATION',
    },
  });

  const { loading, error, data, fetchMore, networkStatus } =
    useQuery<PaginatedStaffingRequestTemplatesResponse>(
      PAGINATED_STAFFING_REQUEST_TEMPLATES_QUERY,
      {
        variables: {
          ...paginationVariables,
          filters,
        },
        notifyOnNetworkStatusChange: true,
      }
    );

  useEffect(() => {
    fetchMore({
      variables: {
        ...paginationVariables,
        filters,
      },
      updateQuery: (_prev, { fetchMoreResult }) => {
        return fetchMoreResult;
      },
    });
  }, [fetchMore, paginationVariables, filters]);

  const paginationKey = 'paginatedStaffingRequestTemplates';
  const recordsKey = 'staffingRequestTemplates';

  if (error) {
    return <ErrorHandler error={error} log="StaffingTemplatesTable" />;
  }

  const templates = data ? data![paginationKey][recordsKey] : [];
  const total = data ? data![paginationKey]['totalCount'] : 0;
  const noTemplates =
    networkStatus === NetworkStatus.ready && templates.length === 0;

  const createStaffingRequestTemplateDrawerUrl = buildDialogUrl(
    'staffing-request-template-drawer'
  );

  const createStaffingRequestTemplateLink = (
    <Link to={createStaffingRequestTemplateDrawerUrl}>
      <b>Staffing Request Template</b>
    </Link>
  );

  if (!hasAnyFilter && noTemplates) {
    return (
      <EmptyContent
        imagePath="./empty-requests.svg"
        header="Set the hiring team up for success 💪"
        text={
          <p>
            Begin by adding a {createStaffingRequestTemplateLink} above to get
            started on your hiring journey with Works.
          </p>
        }
      />
    );
  }

  return (
    <StaffingRequestTemplateList
      filters={filters}
      loading={loading}
      staffingRequestTemplates={templates}
      onTableChange={tableChangeHandler}
      onFilterChange={filterChangeHandler}
      pagination={{ ...paginationVariables, total }}
      localStorageFilterKey={localStorageFilterKey}
    />
  );
};
